import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom"; 
import { 
  Home, UserCircle, Microscope, Users, FileText, MapPin, Menu
} from "lucide-react";
import NavbarMobile from "./NavbarMobile"; 
import NavbarDesktop from "./NavbarDesktop";
import NavbarLogo from "./NavbarLogo"; // 1. Import komponen Logo
import { getNavbarData } from "../../api/navbarApi"; 
import { useSettings } from "../../context/SettingsContext";

const iconMap = {
  Home, About: UserCircle, ScientificProgram: Microscope, 
  Speaker: Users, PosterGuide: FileText, Venue: MapPin 
};

const Navbar = ({ toggleTheme, theme }) => {
  const { logo } = useSettings(); 
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(""); 
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userImg, setUserImg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("eventManagementAuthToken");
    const name = localStorage.getItem("eventManagementAUserName");
    const imgUrl = localStorage.getItem("eventManagementAUserImage");
    
    if (token) {
      setIsLoggedIn(true);
      setUserName(name || "User");
      setUserImg(imgUrl || "");
    } else {
      setIsLoggedIn(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    navigate("/login");
  };

  useEffect(() => {
    let isMounted = true;
    getNavbarData().then((data) => {
      if (isMounted) {
        const uniqueData = [...new Map(data.map(item => [item.id, item])).values()];
        setNavItems(uniqueData);
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const handleScrollTo = (id) => {
    const targetId = id.toLowerCase();
    
    if (id === "Speaker" || id === "Venue") {
      navigate(`/${targetId}s`);
      return;
    }

    if (location.pathname !== "/") {
      navigate(`/#${targetId}`); 
    } else {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, null, `/#${targetId}`);
        setActiveSection(id);
      }
    }
  };

  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();
    const currentHash = location.hash.replace("#", "").toLowerCase();

    if (currentPath === "/login") {
      setActiveSection("Login");
      return;
    }
    if (currentPath === "/register") {
      setActiveSection("Register"); 
      return;
    }
    
    if (currentPath === "/" && currentHash) {
      const matchingItem = navItems.find(item => item.id.toLowerCase() === currentHash);
      if (matchingItem) setActiveSection(matchingItem.id);
      else setActiveSection(""); 
    } else if (currentPath === "/") {
      setActiveSection("Home");
    } else {
      const currentPathLower = location.pathname.toLowerCase();
      const currentItem = navItems.find((item) => {
        const targetPath = `/${item.id.toLowerCase()}`;
        return currentPathLower === targetPath || currentPathLower === `${targetPath}s`;
      });

      if (currentItem) {
        setActiveSection(currentItem.id);
      } else {
        setActiveSection(""); 
      }
    }
  }, [location.pathname, location.hash, navItems]);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-500 font-sans">
      {/* DESKTOP SIDEBAR */}
      <NavbarDesktop 
        navItems={navItems}
        activeSection={activeSection}
        handleScrollTo={handleScrollTo}
        isLoggedIn={isLoggedIn}
        imgSrc={userImg}
        userName={userName}
        handleLogout={handleLogout}
        renderLogo={() => <NavbarLogo loading={loading} logo={logo} />}
      />

      {/* MOBILE SIDEBAR */}
      <NavbarMobile 
        navItems={navItems} 
        activeSection={activeSection} 
        scrollTo={handleScrollTo} 
        renderLogo={() => <NavbarLogo loading={loading} logo={logo} />}
        renderIcon={(name) => {
          const IconComponent = iconMap[name] || Menu;
          return <IconComponent size={20} />; 
        }} 
        isLoggedIn={isLoggedIn}
        userName={userName}
        imgSrc={userImg}
        handleLogout={handleLogout}
      />
    </nav>
  );
};

export default Navbar;